import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useInteractiveWidgetSubmit from '../../hooks/useInteractiveWidgetSubmit';
import { ActionButtons } from '../../../../shared/campaign/ActionButtons';
import { APPROVAL_VARIANTS } from '../../utils/approvalIntegration.config';
import { usePaywall } from '../../../../contexts/PaywallContext/usePaywall';
import { getArtGenerationAvailability } from '../../../../utils/artConsumption';

const CONSUMING_ACTIONS = {
  strategy: ['APPROVED'],
  arts: ['REJECT'],
  batchConfirm: ['CONFIRM'],
  batchRejectConfirm: ['CONFIRM'],
};

const openArtLimitPaywall = (openPaywall, closePaywall) => {
  openPaywall({
    reason: 'art_generation_limit_reached',
    returnTo: window.location.pathname,
    onDismiss: async () => {
      window.dispatchEvent(new CustomEvent('user-plan-count-updated'));
      closePaywall();
    },
  });
};

export default function ApprovalIntegration({
  variant = 'strategy',
  isReadOnly = false,
  toolCallId = null,
  onSideEffect = null,
  requiredArts = 0,
}) {
  const { t } = useTranslation();
  const config = APPROVAL_VARIANTS[variant] || APPROVAL_VARIANTS.strategy;
  const { openPaywall, closePaywall } = usePaywall();

  const options = useMemo(() => {
    if (variant !== 'arts') {
      return config.options;
    }
    return config.options.map((option) =>
      option.type === 'batch_edit'
        ? {...option, name: t('chatsocket.buttons.batch_edit')}
        : option,
    );
  }, [config.options, variant, t]);

  const { handleSubmit: handleComplete, isPending } = useInteractiveWidgetSubmit(
    config.toolName,
    toolCallId,
    (data) => config.buildMessage(data.action),
  );

  const handleSelectOption = useCallback(
    async (_label, type) => {
      const action = config.mapAction(type);
      if (action === null || action === undefined) {
        return;
      }

      const isArtsBatchEdit =
        variant === 'arts' && action === 'EDIT' && requiredArts > 0;
      const consumesArts =
        requiredArts > 0 && CONSUMING_ACTIONS[variant]?.includes(action);

      if (isArtsBatchEdit || consumesArts) {
        const availability = await getArtGenerationAvailability(requiredArts, {
          allowCampaignGeneration: true,
        });
        if (!availability.hasAvailableArts) {
          openArtLimitPaywall(openPaywall, closePaywall);
          if (consumesArts && !isArtsBatchEdit) {
            return;
          }
        }
      }

      onSideEffect?.(variant, action);
      handleComplete({ action });
    },
    [
      config,
      variant,
      requiredArts,
      onSideEffect,
      handleComplete,
      openPaywall,
      closePaywall,
      options,
    ],
  );

  if (isReadOnly || isPending) {
    return null;
  }

  return (
    <ActionButtons
      layout="inline"
      options={options}
      onSelectOption={handleSelectOption}
    />
  );
}
